// =============================================================================
// Module Core - Hook: useMembersStore
// Gestion du state des membres d'organisation
// IMPORTANT: Ce store n'importe PAS supabase directement
// =============================================================================

import { useState, useCallback } from 'react';
import {
  fetchOrganizationMembers,
  fetchMemberByUserId,
  fetchMemberById,
  updateMemberStatus,
  inviteMember,
} from '../data/memberRepository';
import { fetchMemberRoles, changeMemberRole as changeMemberRoleApi } from '../data/roleRepository';
import type { AppRole } from '../access-control/definitions';
import type { 
  OrganizationMemberWithProfile, 
  OrganizationMember,
  MemberStatus,
  Role 
} from '../types';

export function useMembersStore(orgId: string | undefined) {
  const [members, setMembers] = useState<OrganizationMemberWithProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // LOAD MEMBERS
  // ─────────────────────────────────────────────────────────────────────────
  const loadMembers = useCallback(async (status?: MemberStatus | MemberStatus[]) => {
    if (!orgId) {
      setMembers([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchOrganizationMembers(orgId, status);
      setMembers(data);
    } catch (err: any) {
      const msg = err?.message || 'Erreur inconnue';
      console.error('Error loading members:', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // ─────────────────────────────────────────────────────────────────────────
  // GET CURRENT USER MEMBER
  // ─────────────────────────────────────────────────────────────────────────
  const getCurrentUserMember = useCallback(async (
    userId: string
  ): Promise<OrganizationMember | null> => {
    if (!orgId) return null;
    return fetchMemberByUserId(orgId, userId);
  }, [orgId]);

  // ─────────────────────────────────────────────────────────────────────────
  // GET MEMBER BY ID
  // ─────────────────────────────────────────────────────────────────────────
  const getMemberById = useCallback(async (
    memberId: string
  ): Promise<OrganizationMemberWithProfile | null> => {
    return fetchMemberById(memberId);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // GET MEMBER ROLES
  // ─────────────────────────────────────────────────────────────────────────
  const getMemberRoles = useCallback(async (memberId: string): Promise<Role[]> => {
    return fetchMemberRoles(memberId);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // UPDATE STATUS (optimistic)
  // ─────────────────────────────────────────────────────────────────────────
  const changeMemberStatus = useCallback(async (
    memberId: string,
    newStatus: MemberStatus
  ): Promise<{ success: boolean; error?: string }> => {
    if (!orgId) return { success: false, error: 'No organization' };
    
    const previousMembers = [...members];
    
    // Optimistic update
    setMembers(prev => 
      prev.map(m => m.id === memberId ? { ...m, status: newStatus } : m)
    );

    const result = await updateMemberStatus(orgId, memberId, newStatus);
    
    if (!result.success) {
      // Rollback
      setMembers(previousMembers);
    }

    return result;
  }, [members, orgId]);

  // ─────────────────────────────────────────────────────────────────────────
  // INVITE MEMBER
  // ─────────────────────────────────────────────────────────────────────────
  const invite = useCallback(async (
    email: string,
    role: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!orgId) return { success: false, error: 'No organization' };
    return inviteMember(orgId, email, role);
  }, [orgId]);

  // ─────────────────────────────────────────────────────────────────────────
  // CHANGE MEMBER ROLE
  // ─────────────────────────────────────────────────────────────────────────
  const changeMemberRole = useCallback(async (
    memberId: string,
    newRoleCode: AppRole
  ): Promise<{ success: boolean; error?: string }> => {
    if (!orgId) return { success: false, error: 'No organization' };
    return changeMemberRoleApi(orgId, memberId, newRoleCode);
  }, [orgId]);

  // ─────────────────────────────────────────────────────────────────────────
  // CLEAR
  // ─────────────────────────────────────────────────────────────────────────
  const clearMembers = useCallback(() => {
    setMembers([]);
    setError(null);
  }, []);

  return {
    members,
    loading,
    error,
    loadMembers,
    getCurrentUserMember,
    getMemberById,
    getMemberRoles,
    changeMemberStatus,
    changeMemberRole,
    invite,
    clearMembers,
  };
}
